/**
 * @fileoverview AppErrorBoundary — the ONE app-level error boundary every web app mounts around its
 *   routes (MUTATION-INSTRUMENT-001 F-TOCONTAIN-3 U2b-2).
 * @package @puffin-core/page-shell
 *
 * Before it, four web apps (console, patrol-web, payday-web, purchase-web) had no boundary at all —
 * one render error blanked the whole app — and five (palm, planner, port, prep, prospect) each
 * carried their own copy of the same class, three with Retry only, two with Retry + Go home, none
 * reporting anywhere but the console. This is that class once:
 *
 *   - the fallback is page-shell's `<ErrorState>` (translated title + message, Retry) with a Go-home
 *     link — a full page load to the app root, which clears whatever state crashed;
 *   - Retry re-renders the subtree, so a transient failure recovers in place;
 *   - it does NOT report the error. The React root's `onCaughtError`
 *     (`@puffin-core/logging-browser` `createReactRootErrorOptions`, which every root passes —
 *     `reactRootErrorsReported` contract) reports every error ANY boundary catches, exactly once;
 *     a `componentDidCatch` here would ship it twice.
 *
 * Mount it INSIDE the app's providers (i18n, auth, router) and around its routes, so the fallback
 * renders translated and the app's chrome survives the crash of one page.
 *
 * A class, because React's error-boundary API is class-only; the fallback is a function component so
 * it can hold `useShellTranslation()`.
 */

import { Component } from 'react';
import PropTypes from 'prop-types';
import { ErrorState } from './ErrorState.jsx';
import { useShellTranslation } from '../hooks/useShellTranslation.js';

/**
 * @param {{homeHref: string, onRetry: Function}} props
 * @returns {React.JSX.Element}
 */
function AppErrorFallback({ homeHref, onRetry }) {
  const t = useShellTranslation();
  return (
    <div data-testid="app-error-boundary">
      <ErrorState
        title={t('appErrorBoundary.title', { defaultValue: 'Something went wrong' })}
        message={t('appErrorBoundary.message', {
          defaultValue: 'An unexpected error occurred. Please try again.',
        })}
        onRetry={onRetry}
        actions={
          <a className="btn btn-secondary" href={homeHref} data-testid="app-error-boundary-home">
            {t('appErrorBoundary.home', { defaultValue: 'Go to home' })}
          </a>
        }
      />
    </div>
  );
}

AppErrorFallback.propTypes = {
  homeHref: PropTypes.string.isRequired,
  onRetry: PropTypes.func.isRequired,
};

export class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  handleRetry() {
    this.setState({ failed: false });
  }

  render() {
    if (this.state.failed) {
      const { homeHref = '/' } = this.props;
      return <AppErrorFallback homeHref={homeHref} onRetry={this.handleRetry} />;
    }
    return this.props.children;
  }
}

AppErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  /** Where Go home leads — the app's root by default. */
  homeHref: PropTypes.string,
};

export default AppErrorBoundary;
